import instance, { isAxiosErrorHandled } from 'api';
import Button from 'components/button/Button';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ApiResponse } from 'models/formBuilder';
import { useDispatch } from 'react-redux';
import FormTextField from 'components/form/FormTextField';
import { SelectOption } from 'models/formSingleSelectField';
import { WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import { Spinner } from 'components/spinner/Spinner';
import { Toast } from 'helpers/toast';
import { useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';
import { ModalTypes, openModal } from 'reducers/modal';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { getErrorMessage } from 'errors';
import { TFAField } from 'components/form/TFAField';
import { useTFAField } from 'helpers/useTFAField';
import { useNavigate } from '@reach/router';
import { AccountLink } from 'components/sideMenu/SideMenu';

enum TransferFundsTypes {
    TopUpCard = "TopUpCard",
    TopUpAccount = "TopUpAccount",
    CardToCard = "CardToCard"
}

type Card = {
    cardNumber: string,
    nameOnCard: string,
    productDisplayName?: string,
    id: number,
    childElements: []
}

const initialValues = {
    customerAssetAccountsId: 0,
    amount: undefined,
    transferTypes: "",
    SourceCardId: null,
    DestinationCardId: null,
    reference: "",
    tfaCode: '',
    tfaType: 'AuthenticatorApp',
    showTfa: false
};

type FormValues = {
    customerAssetAccountsId: number,
    amount: number | undefined,
    transferTypes: string,
    SourceCardId: number | null,
    DestinationCardId: number | null,
    reference: string,
    tfaCode: string,
    tfaType: string,
    showTfa: boolean
}

const validationSchema = (hasUSDAccounts: boolean, isGirasolCard: boolean) => Yup.object({
    transferTypes: Yup.string()
        .required("Please choose a transfer type")
        .oneOf(
            [
                TransferFundsTypes.TopUpCard, 
                ...(!isGirasolCard ? [TransferFundsTypes.TopUpAccount] : []),
                ...(hasUSDAccounts ? [TransferFundsTypes.CardToCard] : [])
            ],
            "Invalid transfer type"
        ),

    amount: Yup.number()
        .typeError("Please enter a valid amount")
        .required("Please choose an amount")
        .moreThan(0, "Please choose an amount greater than 0"),

    reference: Yup.string()
        .trim()
        .required("Please enter a transaction reference"),

    customerAssetAccountsId: Yup.number().when("transferTypes", {
        is: (type: string) => type !== TransferFundsTypes.CardToCard,
        then: (schema) =>
            schema
                .required("Please choose an account")
                .moreThan(0, "Please choose an account"),
        otherwise: (schema) => schema.notRequired(),
    }),

    SourceCardId: Yup.number().when("transferTypes", {
        is: (type: string) => type !== TransferFundsTypes.TopUpCard,
        then: (schema) =>
            schema
                .required("Please select a source card")
                .nullable(false),
        otherwise: (schema) => schema.nullable(),
    }),

    DestinationCardId: Yup.number().when("transferTypes", {
        is: (type: string) => type !== TransferFundsTypes.TopUpAccount,
        then: (schema) =>
            schema
                .required("Please select a destination card")
                .nullable(false),
        otherwise: (schema) => schema.nullable(),
    }),
    tfaCode: Yup.string().when("showTfa", {
        is: true,
        then: (schema) =>
            schema
                .required("Please enter your TFA code")
                .min(6, "Please enter your TFA code"),
        otherwise: (schema) => schema.notRequired(),
    }),

});


export const TopUpCard = ({
    cardId,
    onCardToppedUp,
}: {
    cardId: number;
    onCardToppedUp: () => void;
}) => {
    const { t } = useTranslation('common');
    const [errorMessage, setErrorMessage] = useState('');
    const [accountOptions, setAccountOptions] = useState<SelectOption[]>([]);
    const dispatch = useDispatch();
    const { customerMenuLinks } =
        useSelector(selectComponentResources);
    const cardList = customerMenuLinks?.filter((link) => link.elementType === "Cards")[0]
        .accounts?.filter((card) => !card.childElements.some(x => x.path.includes("/activate-card")))

        const currentCard = useMemo(() => {
            return cardList?.find((card: any) => card.id === cardId);
        }, [cardList, cardId]);
        const isGirasolCard = useMemo(() => {
            return (currentCard as any)?.productDisplayName === 'Girasol';
        }, [currentCard]);

    const tempUsdDusdAccounts = useMemo((): SelectOption[] => {
        const fundingSection = customerMenuLinks?.find((link) => link.displayName === "Funding");
        const cryptoAccountsSection = customerMenuLinks?.find((link) => link.displayName === "Crypto Accounts");

        const fundingAccounts = fundingSection?.accounts || [];
        const cryptoAccounts = cryptoAccountsSection?.accounts || [];

        const usdAccounts = fundingAccounts.filter((account: any) => (account as any).currencyCode === "USD" && (account as any).accountType !== "DDA").map((account: any) => ({
            value: account.id,
            label: account.currencyCode,
        }));

        const dusdAccounts = cryptoAccounts
            .filter((account: any) => (account as any).code === "dUSD")
            .map((account: any) => ({
                value: account.id,
                label: account.code,
            }));

        // Girasol → ONLY USD
        if (isGirasolCard) {
            return usdAccounts;
        }
        return [...usdAccounts, ...dusdAccounts];
    }, [customerMenuLinks]);

    useEffect(() => {
        setAccountOptions(tempUsdDusdAccounts);
    }, [tempUsdDusdAccounts]);

    const hasUSDAccounts = tempUsdDusdAccounts.some(account => account.label === "USD");
    const transferType = useMemo(() => {
        const types = [
            { value: 'TopUpCard', label: t('transferType_topUpCard') },
        ];

        // Only show TopUpAccount for CRB cards (not Girasol)
        if (!isGirasolCard) {
            types.push({ value: 'TopUpAccount', label: t('transferType_topUpAccount') });
        }

        // Show CardToCard if has USD accounts
        if (hasUSDAccounts) {
            types.push({ value: 'CardToCard', label: t('transferType_cardToCard') });
        }

        return types;
    }, [isGirasolCard, hasUSDAccounts, t]);

    const finalCardList: SelectOption[] = (cardList as Card[] | undefined)?.map((card) => ({
        label: `${card.nameOnCard} (${card.cardNumber})`,
        value: card.id
    })) ?? [];
    const [tfaType, toggleTfaType] = useTFAField();
    const [showTfa, setShowTfa] = useState(false)
    const [isModalClosed, setIsModalClosed] = useState(false)
    const navigate = useNavigate()


    const handleSubmit = async (
        values: typeof initialValues,
        formikHelpers: FormikHelpers<typeof initialValues>
    ) => {
        const currencyType = accountOptions.find((card) => card.value === values.customerAssetAccountsId)?.label;

        const CardToCard = {
            SourceCardId: values.SourceCardId,
            DestinationCardId: values.DestinationCardId,
        };

        const TopUpCard = {
            CustomerAssetAccountId: values.customerAssetAccountsId,
            DestinationCardId: values.DestinationCardId ?? values.SourceCardId,
        };

        const TopUpAccount = {
            CustomerAssetAccountId: values.customerAssetAccountsId,
            SourceCardId: values.SourceCardId,
        };

        const finalPayload = {
            Amount: values.amount,
            Reference: values.reference,
            transferTypes: values.transferTypes,
            ...(values.transferTypes !== TransferFundsTypes.CardToCard ? { currencyType } : {}),
            ...(values.transferTypes === TransferFundsTypes.CardToCard ? CardToCard : {}),
            ...(values.transferTypes === TransferFundsTypes.TopUpAccount ? TopUpAccount : {}),
            ...(values.transferTypes === TransferFundsTypes.TopUpCard ? TopUpCard : {}),
            tfaDetails: {
                tfaCode: values.tfaCode,
                tfaType: values.tfaType,
            },
        };

        setErrorMessage('');

        try {
            const res = await instance.post(endpoints.cards.transferfunds, finalPayload);
            Toast.openSuccessToast('Top up successful');
            onCardToppedUp();
        } catch (err) {
            if (isAxiosErrorHandled(err)) {
                const errors = err?.response?.data?.errors ?? [];
                if (errors.some((error) => toCamelCase(error.fieldName) in initialValues)) {
                    errors.forEach((error) =>
                        formikHelpers.setFieldError(
                            toCamelCase(error.fieldName) as string,
                            getErrorMessage(error.messageCode)
                        )
                    );
                } else {
                    Toast.openErrorToast(getErrorMessage(errors[0].messageCode));
                }
            } else {
                Toast.openGenericErrorToast();
            }

            // 🔹 Reset TFA so user can try again from start
            setShowTfa(false);
        } finally {
            // 🔹 Always allow retry
            formikHelpers.setSubmitting(false);
        }
    };

    const renderCardToCard = (values: FormValues) => {
        const filteredCardList = finalCardList.filter((card) => card.value !== values.SourceCardId)
        return (
            <>
                <WrappedFormSingleSelectField
                    fieldName="SourceCardId"
                    options={finalCardList}
                    label="Source Card"
                    readOnly={true}
                />
                <WrappedFormSingleSelectField
                    fieldName="DestinationCardId"
                    options={filteredCardList}
                    label="Destination Card"
                />
            </>
        )
    }

    const renderTopUpAccount = () => {
        return (
            <>
                <WrappedFormSingleSelectField
                    fieldName="SourceCardId"
                    options={finalCardList}
                    label="Source Card"
                    readOnly={true}
                />
                <WrappedFormSingleSelectField
                    fieldName="customerAssetAccountsId"
                    options={accountOptions}
                    label="Destination Account"
                />
            </>
        )
    }

    const renderTopUpCard = () => {
        return (
            <>
                <WrappedFormSingleSelectField
                    fieldName="customerAssetAccountsId"
                    options={accountOptions}
                    label="Source Account"
                />
                <WrappedFormSingleSelectField
                    fieldName="SourceCardId"
                    options={finalCardList}
                    label="Destination Card"
                    readOnly={true}

                />

            </>
        )
    }

    const openReviewModal = (values: FormValues) => {
        const temp = accountOptions.find(
            a => a.value === values.customerAssetAccountsId
        )?.label?.trim();

        const isRealUSD = temp === "USD" || temp?.startsWith("USD Account");

        const currencyType = isRealUSD ? "USD" : "dUSD";

        const CardToCard = {
            "SourceCardId": values.SourceCardId,
            "DestinationCardId": values.DestinationCardId,
        }

        const TopUpCard = {
            "CustomerAssetAccountId": values.customerAssetAccountsId,
            "DestinationCardId": values.DestinationCardId ?? values.SourceCardId,
        }

        const TopUpAccount = {
            "CustomerAssetAccountId": values.customerAssetAccountsId,
            "SourceCardId": values.SourceCardId,
        }

        const finalPayload = {
            Amount: values.amount,
            Reference: values.reference,
            transferTypes: values.transferTypes,
            ...(values.transferTypes !== TransferFundsTypes.CardToCard
                ? { currencyType }
                : {}),
            ...(values.transferTypes === TransferFundsTypes.CardToCard
                ? CardToCard
                : {}),
            ...(values.transferTypes === TransferFundsTypes.TopUpAccount
                ? TopUpAccount
                : {}),
            ...(values.transferTypes === TransferFundsTypes.TopUpCard
                ? TopUpCard
                : {}),
        };

        dispatch(
            openModal({
                modalType: ModalTypes.REVIEW_MODAL,
                data: {
                    payload: finalPayload,
                    setIsModalClosed
                }
            })
        );
    };

    const renderButton = (
        values: FormValues,
        isSubmitting: boolean,
        isValid: boolean
    ) => {
        const isCardToCard = values.transferTypes === TransferFundsTypes.CardToCard;

        if (!isCardToCard) {
            return (
                <Button
                    variety="full"
                    type="button"
                    disabled={isSubmitting} // ✅ Only disable while API call is in flight
                    onClick={() => openReviewModal(values)}
                >
                    Review Order
                </Button>
            );
        }

        return (
            <Button
                variety="full"
                type={showTfa ? "submit" : "button"}
                disabled={isSubmitting} // ✅ Allow clicking if not submitting
                onClick={(e) => {
                    if (!showTfa) {
                        e.preventDefault();
                        if (!isSubmitting) setShowTfa(true);
                    }
                }}
            >
                {isSubmitting ? <Spinner /> : showTfa ? "Confirm Top Up" : "Top Up"}
            </Button>
        );
    };

    useEffect(() => {
        if (isModalClosed) {
            const newPath = window.location.pathname.replace('/top-up', '/card-transaction-history');
            navigate(newPath)
        }
    }, [isModalClosed])


    return (
        <div className="TopUpCardPage FormPage">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema(hasUSDAccounts, isGirasolCard)}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values, isValid, setFieldValue }) => (
                    <Form>
                        <FormikFieldSetter cardId={cardId} finalCardList={finalCardList} showTfa={showTfa} accountOptions={accountOptions} isGirasolCard={isGirasolCard}/>
                        <WrappedFormSingleSelectField
                            fieldName="transferTypes"
                            options={transferType}
                            label="Select Transfer Type"
                        />
                        {values.transferTypes === TransferFundsTypes.CardToCard && renderCardToCard(values)}
                        {values.transferTypes === TransferFundsTypes.TopUpAccount && renderTopUpAccount()}
                        {values.transferTypes === TransferFundsTypes.TopUpCard && renderTopUpCard()}

                        <FormTextField
                            field="amount"
                            required={true}
                            label="Top Up Amount"
                            type="number"
                        />
                        <FormTextField
                            field="reference"
                            required={true}
                            label="Transaction Reference"
                            type="text"
                        />
                        {showTfa && <TFAField
                            field={'tfaCode'}
                            label={'TFA Code'}
                            required={true}
                            toggleTFAType={
                                toggleTfaType
                                    ? () => {
                                        toggleTfaType();
                                        setFieldValue(
                                            'tfaType',
                                            values.tfaType
                                        );
                                    }
                                    : null
                            }
                            tfaType={tfaType}
                            holdFocus
                            autoFocus
                        />}
                        {renderButton(values, isSubmitting, isValid)}
                        {errorMessage && <div className="ErrorLabel">{errorMessage}</div>}
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const FormikFieldSetter = ({
    cardId,
    finalCardList,
    showTfa,
    accountOptions,
    isGirasolCard,
}: {
    cardId: number;
    finalCardList: SelectOption[];
    showTfa: boolean;
    accountOptions: SelectOption[];
    isGirasolCard: boolean;

}) => {
    const { setFieldValue, values } = useFormikContext<FormValues>();

    useEffect(() => {
        const sourceCard = finalCardList.find((card) => card.value === cardId);
        if (sourceCard) {
            setFieldValue("SourceCardId", sourceCard.value);
        }
        if (values.transferTypes === TransferFundsTypes.TopUpCard && cardId) {
            setFieldValue("DestinationCardId", cardId);
        }
    }, [values.transferTypes, finalCardList, cardId, setFieldValue]);

    useEffect(() => {
        setFieldValue("showTfa", showTfa);
    }, [showTfa]);

    useEffect(() => {
        if (
            isGirasolCard &&
            values.customerAssetAccountsId &&
            accountOptions.some(
                a => a.label === 'dUSD' && a.value === values.customerAssetAccountsId
            )
        ) {
            setFieldValue('customerAssetAccountsId', 0);
        }
    }, [isGirasolCard, accountOptions, values.customerAssetAccountsId, setFieldValue]);
    return null;
};
